import { useState } from "react";
import toast from "react-hot-toast";

import { LoadingBar } from "components/common/basic";
import { FileUpload } from "components/common/composite";
import { AttachmentFragment } from "graphql/fragments";
import { Base64AttachmentAttributes } from "graphql/types";
import useErrorLogger from "hooks/useErrorLogger";
import { useTranslation } from "translations";
import { tw } from "utils/tw";

import Attachment from "./Attachment";
import Base64Attachment from "./Base64Attachment";

interface Props {
  files: AttachmentFragment[] | Base64AttachmentAttributes[];
  createFile: (attributes: {
    file: Base64AttachmentAttributes;
    filename: string;
  }) => void;
  destroyFile: (id: string | number) => void;
  shouldDestroyWithoutConfirmation?: boolean;
  // FileUpload props
  illustration: {
    src: string;
    alt: string;
  };
  fileRequirementsText: string;
  validFileTypes?: string[];
  isImageUpload?: boolean;
  allowMultiple?: boolean;
  hasEditAccess?: boolean;
  isFullWidth?: boolean;
}

export default ({
  files,
  createFile,
  destroyFile,
  shouldDestroyWithoutConfirmation,
  hasEditAccess = true,
  isFullWidth = false,
  ...fileUploadProps
}: Props): React.JSX.Element => {
  const [isCreating, setIsCreating] = useState(false);

  const { t } = useTranslation("common");
  const { reportError } = useErrorLogger();

  const uploadFiles = async (files: File[]) => {
    if (files.length < 1) return;

    setIsCreating(true);

    try {
      const promisedAttributes: Promise<{
        file: Base64AttachmentAttributes;
        filename: string;
      }>[] = files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
              if (typeof event.target?.result !== "string") {
                reject(new Error(`Invalid file data for ${file.name}`));
                return;
              }

              const attributes = {
                file: { data: event.target.result, filename: file.name },
                filename: file.name,
              };
              resolve(attributes);
            };

            reader.onerror = () =>
              reject(new Error(`Failed to read file: ${file.name}`));

            reader.readAsDataURL(file);
          })
      );

      const contents = await Promise.all(promisedAttributes);
      const promisedFiles = contents.map((attributes) =>
        createFile(attributes)
      );

      await Promise.all(promisedFiles);
    } catch (error) {
      reportError("attachment upload", error);
      toast.error(
        t("attachments.uploadError", "Upload failed. Please try again.")
      );
    } finally {
      setIsCreating(false);
    }
  };

  const isBase64Attachments =
    files.length > 0 && Object.prototype.hasOwnProperty.call(files[0], "data");

  return (
    <div className={tw("flex", "flex-col", "space-y-4")}>
      {hasEditAccess && (
        <FileUpload
          {...fileUploadProps}
          id="document-upload"
          selectFile={uploadFiles}
          clearFile={() => null}
          maxSize={10_000_000}
        />
      )}

      <div
        className={tw("-mr-4", "-mb-4", "flex", "justify-start", "flex-wrap")}
      >
        {isBase64Attachments
          ? (files as Base64AttachmentAttributes[]).map((file, index) => (
              <div
                key={index}
                className={tw("mr-4", "mb-4", { "flex-1": isFullWidth })}
              >
                <Base64Attachment
                  file={file}
                  destroyFile={
                    hasEditAccess ? () => destroyFile(index) : undefined
                  }
                  fallbackImageSrc={fileUploadProps.illustration.src}
                />
              </div>
            ))
          : (files as AttachmentFragment[]).map((file) => (
              <div
                key={file.id}
                className={tw("mr-4", "mb-4", { "flex-1": isFullWidth })}
              >
                <Attachment
                  file={file}
                  filename={file.filename ?? "uploaded file"}
                  destroyFile={
                    hasEditAccess ? () => destroyFile(file.id) : undefined
                  }
                  skipConfirmation={shouldDestroyWithoutConfirmation}
                />
              </div>
            ))}
      </div>

      {isCreating && (
        <LoadingBar
          message={t("attachments.loadingBar.label", "Uploading attachment")}
        />
      )}
    </div>
  );
};
